import { Component, EventEmitter, Inject, Optional, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ClassificationService } from '../work-classification.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.reducer';
import { selectCurrentProject, selectCurrentProjectId, selectSelectableProjects } from 'src/app/state/app.selectors';
import { WorkClassification } from 'src/app/shared/Models/hourentry';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

interface WorkClassificationWithLevel extends WorkClassification {
  level: number;
}
@Component({
    selector: 'work-classification-form',
    templateUrl: './work-classification-form.component.html',
    styleUrls: ['./work-classification-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WorkClassificationFormComponent {
  @Output() classificationCreated = new EventEmitter<WorkClassification>();

  classificationForm: FormGroup;
  projects = this.store.select(selectSelectableProjects);
  currentProjectId$ = this.store.select(selectCurrentProjectId);
  currentProject$ = this.store.select(selectCurrentProject);
  classifications: WorkClassification[] = []; // Hierarchical list from the API
  flattenedClassifications: WorkClassificationWithLevel[] = []; // Flattened list for dropdown
  parentId = 0;
  isLoading = false;
  errorMessage: string | null = null;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private classificationService: ClassificationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { classification: WorkClassification | null, parentId: number }
  ) {
    this.classificationForm = this.fb.group({
      name: ['', Validators.required],
      parentId: [null],
      projectId: [null, Validators.required],
      description: ['']
    });
  }

  ngOnInit(): void {
    // Get parentId from query params if present
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe(params => {
        if (params.parentId) {
          this.parentId = params.parentId;
          this.classificationForm.get('parentId')?.setValue(params.parentId);
        }
      })
    );

    // Set parentId if passed in dialog data
    if (this.data && this.data.parentId) {
      this.classificationForm.get('parentId')?.setValue(this.data.parentId);
    }

    // Set existing classification values if editing
    if (this.data && this.data.classification) {
      const classification = this.data.classification;
      this.classificationForm.patchValue({
        name: classification.name,
        parentId: classification.parentId,
        projectId: classification.projectId,
        description: classification.description
      });
    }

    // Load classifications for the current project
    this.subscriptions.add(
      this.currentProjectId$.subscribe(projectId => {
        if (projectId) {
          this.classificationForm.get('projectId')?.setValue(projectId);
          this.loadClassifications(projectId);
        }
      })
    );
  }

  loadClassifications(projectId: number): void {
    this.classificationService.getClassifications(projectId).subscribe(
      classifications => {
        this.classifications = classifications; // Save hierarchical structure
        this.flattenedClassifications = this.flattenClassifications(classifications); // Flatten the structure for dropdown
      },
      error => {
        this.errorMessage = 'Failed to load classifications. Please try again.';
      }
    );
  }

  flattenClassifications(classifications: WorkClassification[], level: number = 0): WorkClassificationWithLevel[] {
    let flatList: WorkClassificationWithLevel[] = [];

    for (const classification of classifications) {
      flatList.push({ ...classification, level }); // Add current classification to the flat list
      if (classification.children && classification.children.length > 0) {
        flatList = flatList.concat(this.flattenClassifications(classification.children, level + 1)); // Recursively add children
      }
    }

    return flatList;
  }
  getIndentedName(classification: WorkClassificationWithLevel): string {
    return '—'.repeat(classification.level) + ' ' + classification.name;
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onSubmit(): void {
    if (this.classificationForm.valid) {
      this.isLoading = true;
      const classification: WorkClassification = { ...this.classificationForm.value };

      if (this.data && this.data.classification) {
        // Update existing classification
        classification.id = this.data.classification.id;
        this.classificationService.updateClassification(classification).subscribe(
          (updatedClassification) => {
            this.isLoading = false;
            this.dialog.closeAll();
            this.classificationCreated.emit(updatedClassification);
          },
          error => {
            this.isLoading = false;
            this.errorMessage = 'Failed to update classification. Please try again.';
          }
        );
      } else {
        // Create new classification
        this.classificationService.createClassification(classification).subscribe(
          (newClassification) => {
            this.isLoading = false;
            this.dialog.closeAll();
            this.classificationCreated.emit(newClassification);
          },
          error => {
            this.isLoading = false;
            this.errorMessage = 'Failed to create classification. Please try again.';
          }
        );
      }
    }
  }
}
